/**
 * complete GLB 解析前 occurrence 图分析器。
 *
 * 该类只解释原始 glTF JSON 中由 PuppyCAD 导出器写入的 occurrence 协议，不创建或修改
 * Three.js 对象。业务类型只接受显式 `node_type`；所有未标识 node 都作为 internal 穿透。
 */
export class CompleteGlbOccurrenceGraphAnalyzer {
  /**
   * 分析选中 glTF 子树中的业务 occurrence、definition 共享和 mesh 所有权。
   *
   * @param input 原始 glTF JSON 与已验证的根选择。
   * @returns 可同时供 coverage 和运行时绑定消费的只读 occurrence 图。
   */
  public analyze(
    input: PuppyCAD.CompleteGlbOccurrenceGraphAnalyzeInput,
  ): PuppyCAD.CompleteGlbOccurrenceGraph {
    const nodes = input.json.nodes
    if (!Array.isArray(nodes)) {
      throw new Error('完整 GLB occurrence 分析失败：缺少 nodes 定义')
    }

    const selectedNode = nodes[input.selection.nodeIndex]
    if (!selectedNode) {
      throw new Error(
        `完整 GLB occurrence 分析失败：选中根不存在，nodeIndex=${input.selection.nodeIndex}`,
      )
    }
    const selectedRootInstanceId = this.readRequiredBusinessIdentity(
      selectedNode.extras?.instance_id,
      'instance_id',
      input.selection.nodeIndex,
    )

    const mutableOccurrencesByInstanceId = new Map<string, PuppyCAD.CompleteGlbOccurrenceDescriptor>()
    const mutableOccurrenceByNodeIndex = new Map<number, PuppyCAD.CompleteGlbOccurrenceDescriptor>()
    const mutableOccurrenceInstanceIdsByNodeId = new Map<string, string[]>()
    const meshOwnerByNodeIndex = new Map<number, PuppyCAD.CompleteGlbMeshOwnerDescriptor>()
    const internalNodeIndices = new Set<number>()
    const visitedNodeIndices = new Set<number>()
    const activeNodeIndices = new Set<number>()

    /**
     * 递归解释原始 glTF 子树，并把 internal 层级折叠到最近业务祖先。
     *
     * @param nodeIndex 当前原始 node 索引。
     * @param parentInstanceId 最近的显式业务祖先 occurrence 身份。
     */
    const visit = (nodeIndex: number, parentInstanceId: string | null): void => {
      if (!Number.isInteger(nodeIndex) || nodeIndex < 0 || nodeIndex >= nodes.length) {
        throw new Error(
          `完整 GLB occurrence 分析失败：nodeIndex 非法，nodeIndex=${String(nodeIndex)}`,
        )
      }
      if (activeNodeIndices.has(nodeIndex)) {
        throw new Error(`完整 GLB occurrence 分析失败：检测到环引用，nodeIndex=${nodeIndex}`)
      }
      if (visitedNodeIndices.has(nodeIndex)) {
        throw new Error(`完整 GLB occurrence 分析失败：node 被重复引用，nodeIndex=${nodeIndex}`)
      }

      const node = nodes[nodeIndex]
      if (!node) {
        throw new Error(`完整 GLB occurrence 分析失败：node 不存在，nodeIndex=${nodeIndex}`)
      }

      visitedNodeIndices.add(nodeIndex)
      activeNodeIndices.add(nodeIndex)

      const rawNodeType = node.extras?.node_type
      let currentParentInstanceId = parentInstanceId

      if (rawNodeType === undefined) {
        // 未声明业务类型的 node 只承担矩阵、层级或绘制载体职责，不能进入设计树。
        internalNodeIndices.add(nodeIndex)
      } else {
        if (typeof rawNodeType !== 'string') {
          throw new Error(
            `完整 GLB occurrence 分析失败：node_type 非法，nodeIndex=${nodeIndex}, nodeType=${String(rawNodeType)}`,
          )
        }
        const semanticKind = rawNodeType.trim()
        if (semanticKind !== 'assembly' && semanticKind !== 'part') {
          throw new Error(
            `完整 GLB occurrence 分析失败：node_type 非法，nodeIndex=${nodeIndex}, nodeType=${JSON.stringify(rawNodeType)}`,
          )
        }

        const instanceId = this.readRequiredBusinessIdentity(
          node.extras?.instance_id,
          'instance_id',
          nodeIndex,
        )
        const nodeId = this.readRequiredBusinessIdentity(
          node.extras?.node_id,
          'node_id',
          nodeIndex,
        )
        const duplicated = mutableOccurrencesByInstanceId.get(instanceId)
        if (duplicated) {
          throw new Error(
            `完整 GLB occurrence 分析失败：instance_id 重复，instanceId=${instanceId}, nodeIndices=${duplicated.nodeIndex},${nodeIndex}`,
          )
        }

        const descriptor: PuppyCAD.CompleteGlbOccurrenceDescriptor = {
          nodeIndex,
          instanceId,
          nodeId,
          parentInstanceId,
          childInstanceIds: [],
          semanticKind,
        }
        mutableOccurrencesByInstanceId.set(instanceId, descriptor)
        mutableOccurrenceByNodeIndex.set(nodeIndex, descriptor)

        const definitionOccurrences = mutableOccurrenceInstanceIdsByNodeId.get(nodeId) ?? []
        definitionOccurrences.push(instanceId)
        mutableOccurrenceInstanceIdsByNodeId.set(nodeId, definitionOccurrences)

        if (parentInstanceId) {
          const parent = mutableOccurrencesByInstanceId.get(parentInstanceId)
          if (!parent) {
            throw new Error(
              `完整 GLB occurrence 分析失败：业务父 occurrence 不存在，instanceId=${instanceId}, parentInstanceId=${parentInstanceId}`,
            )
          }
          ;(parent.childInstanceIds as string[]).push(instanceId)
        }

        currentParentInstanceId = instanceId
      }

      if (typeof node.mesh === 'number' && Number.isInteger(node.mesh) && node.mesh >= 0) {
        meshOwnerByNodeIndex.set(
          nodeIndex,
          currentParentInstanceId
            ? { kind: 'occurrence', instanceId: currentParentInstanceId }
            : { kind: 'project-root' },
        )
      }

      for (const childNodeIndex of node.children ?? []) {
        if (
          !Number.isInteger(childNodeIndex)
          || childNodeIndex < 0
          || childNodeIndex >= nodes.length
        ) {
          throw new Error(
            `完整 GLB occurrence 分析失败：child nodeIndex 非法，nodeIndex=${nodeIndex}, childNodeIndex=${String(childNodeIndex)}`,
          )
        }
        visit(childNodeIndex, currentParentInstanceId)
      }

      activeNodeIndices.delete(nodeIndex)
    }

    visit(input.selection.nodeIndex, null)

    const selectedOccurrence = mutableOccurrenceByNodeIndex.get(input.selection.nodeIndex)
    const directChildInstanceIds = new Set<string>(
      selectedOccurrence
        ? selectedOccurrence.childInstanceIds
        : [...mutableOccurrencesByInstanceId.values()]
            .filter(occurrence => occurrence.parentInstanceId === null)
            .map(occurrence => occurrence.instanceId),
    )

    return {
      selection: input.selection,
      selectedRootInstanceId,
      occurrencesByInstanceId: mutableOccurrencesByInstanceId,
      occurrenceByNodeIndex: mutableOccurrenceByNodeIndex,
      occurrenceInstanceIdsByNodeId: mutableOccurrenceInstanceIdsByNodeId,
      directChildInstanceIds,
      meshOwnerByNodeIndex,
      internalNodeIndices,
    }
  }

  /**
   * 读取显式业务节点必须提供的非空稳定身份。
   *
   * @param value 原始协议字段。
   * @param fieldName 错误上下文中的字段名。
   * @param nodeIndex 当前原始 glTF node 索引。
   * @returns 去除首尾空白后的稳定身份。
   */
  private readRequiredBusinessIdentity(
    value: unknown,
    fieldName: 'instance_id' | 'node_id',
    nodeIndex: number,
  ): string {
    if (typeof value !== 'string' || value.trim().length === 0) {
      throw new Error(
        `完整 GLB occurrence 分析失败：业务节点缺少 ${fieldName}，nodeIndex=${nodeIndex}`,
      )
    }
    return value.trim()
  }
}
